import { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Search, X, Check, Plus, TriangleAlert } from 'lucide-react'

import Avatar from '@/elements/Avatar'
import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import Text from '@/elements/Text'
import Label from '@/elements/Label'
import DropdownMenu from '@/components/DropdownMenu'
import { useDropdown } from '@/hooks/useDropdown'
import useBreakpoints from '@/hooks/useBreakpoints'

import './FormSearchableSelect.scss'

interface FormSearchableSelectProps<T> {
  name: string
  label?: string
  options: T[]
  loadingOptions?: boolean
  onSubmit: (value: T | null) => void
  submitLabel?: string
  optionLabel: (item: T) => string
  optionValue: (item: T) => string
  optionImage?: (item: T) => string
  notFetchedData?: boolean
  autoSubmit?: boolean
  updateSearch?: (value: string) => void
  searchResult?: string
  initialValue?: T | null
  isUserSearch?: boolean
  placeholder?: string
  dirty?: boolean
  error?: string
  style?: React.CSSProperties
}

const FormSearchableSelect = <T,>({
  name,
  label,
  options,
  loadingOptions,
  onSubmit,
  submitLabel = 'Add',
  optionLabel,
  optionValue,
  optionImage,
  notFetchedData,
  autoSubmit = true,
  updateSearch,
  searchResult,
  initialValue = null,
  isUserSearch,
  placeholder = 'Search...',
  dirty,
  error,
  style,
}: FormSearchableSelectProps<T>) => {
  const [open, setOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState<T | null>(initialValue)
  const [localSearch, setLocalSearch] = useState('')
  const { isMobile } = useBreakpoints()

  const filteredOptions = useMemo(() => {
    if (!notFetchedData) return options

    const search = localSearch.toLowerCase()
    return options.filter(option =>
      optionLabel(option).toLowerCase().includes(search)
    )
  }, [options, localSearch, notFetchedData, optionLabel])

  const displayValue = useMemo(() => {
    if (selectedValue) return optionLabel(selectedValue)
    return notFetchedData ? localSearch : searchResult || ''
  }, [selectedValue, optionLabel, localSearch, searchResult, notFetchedData])

  const {
    position,
    anchorRef: inputRef,
    menuRef,
    optionRefs,
    focusedIndex,
    onOptionKeyDown,
  } = useDropdown({
    open,
    onClose: () => setOpen(false),
    optionsLength: filteredOptions.length,
    onSelect: index => handleSelect(filteredOptions[index]),
  })

  const handleUpdateSearch = (value: string) => {
    if (notFetchedData) {
      setLocalSearch(value)
      setOpen(true)
      return
    }

    updateSearch?.(value)
    setOpen(Boolean(value.trim()))
  }

  const handleSelect = (option: T) => {
    setSelectedValue(option)
    setOpen(false)
    setLocalSearch('')
    updateSearch?.('')
    inputRef.current?.focus()

    if (notFetchedData || autoSubmit) {
      onSubmit(option)
    }
  }

  const handleRemove = () => {
    setSelectedValue(null)
    setLocalSearch('')
    updateSearch?.('')
    setOpen(false)
    inputRef.current?.focus()
    onSubmit(null)
  }

  const handleSubmit = () => {
    if (!selectedValue) return

    onSubmit(selectedValue)
    setSelectedValue(null)
    setLocalSearch('')
    updateSearch?.('')
    setOpen(false)
    inputRef.current?.focus()
  }

  useEffect(() => {
    setSelectedValue(initialValue)
  }, [initialValue])

  return (
    <Flex
      wfull
      className={classNames('form-field searchable-select', { dirty })}
      style={style}
    >
      <Label htmlFor={name} hidden={!label}>
        {label}
      </Label>

      <Flex wfull>
        <Flex wfull inline={!isMobile} align='center' gap={3}>
          <div ref={inputRef} className='select-input-wrapper' style={{ width: '100%' }}>
            <FormInput
              name={name}
              placeholder={placeholder}
              value={displayValue}
              Icon={!selectedValue ? Search : undefined}
              customIcon={
                isUserSearch && selectedValue ? (
                  <Avatar
                    name={optionLabel(selectedValue)}
                    src={optionImage?.(selectedValue) || ''}
                    size='extraSmall'
                  />
                ) : undefined
              }
              onFocus={() => setOpen(true)}
              onChangeDebounced={handleUpdateSearch}
              className={classNames({ 'has-selected-value': selectedValue })}
              aria-expanded={open}
              aria-autocomplete='list'
              aria-controls={`${name}-listbox`}
              style={{ width: '100%' }}
            />

            {selectedValue && (
              <Button variant='iconBtn' onClick={handleRemove} small>
                <X size={16} />
              </Button>
            )}
          </div>
          {!notFetchedData && !autoSubmit && (
            <Button
              variant='primary'
              onClick={handleSubmit}
              disabled={!selectedValue}
              styles={{ width: isMobile ? '100%' : 'auto' }}
            >
              <Plus size={18} style={{ minWidth: 18 }} />
              {submitLabel}
            </Button>
          )}
        </Flex>
        {error && (
          <span className='field-error'>
            <TriangleAlert size={12} />
            {error}
          </span>
        )}
      </Flex>

      {open &&
        <DropdownMenu
          open={open}
          position={position}
          onClose={() => {
            setOpen(false)
            setLocalSearch('')
            handleUpdateSearch('')
          }}
          title={placeholder}
          menuRef={menuRef as React.RefObject<HTMLDivElement>}
          isSearchable={true}
        >
          {isMobile && (
            <Flex wfull p={2}>
              <FormInput
                className='dropdown-search-input'
                type='text'
                placeholder='Search...'
                value={localSearch}
                onChange={e => handleUpdateSearch(e.target.value)}
                onKeyDown={e => onOptionKeyDown(e, 0)}
                onClick={e => e.stopPropagation()}
                autoFocus
                style={{ width: '100%' }}
              />
            </Flex>
          )}
          {loadingOptions && !notFetchedData ? (
            <Flex p={3}>
              <Text color='black-light-60'>Loading...</Text>
            </Flex>
          ) : filteredOptions.length ? (
            <ul role='listbox' id={`${name}-listbox`}>
              {filteredOptions.map((option, index) => {
                const selected =
                  selectedValue &&
                  optionValue(selectedValue) === optionValue(option)

                return (
                  <li
                    key={optionValue(option)}
                    ref={el => {
                      optionRefs.current[index] = el
                    }}
                    role='option'
                    tabIndex={-1}
                    aria-selected={selected ? 'true' : 'false'}
                    className={classNames('dropdown-item', {
                      active: selected,
                      focused: index === focusedIndex,
                    })}
                    onClick={() => handleSelect(option)}
                    onKeyDown={e => onOptionKeyDown(e, index)}
                  >
                    <Flex inline align='center' gap={2}>
                      {isUserSearch && optionImage && (
                        <Avatar
                          name={optionLabel(option)}
                          src={optionImage(option)}
                          size='extraSmall'
                        />
                      )}
                      {optionLabel(option)}
                    </Flex>
                    {selected && <Check size={16} />}
                  </li>
                )
              })}
            </ul>
          ) : (
            <Flex p={3}>
              <Text color='black-light-60'>No results found.</Text>
            </Flex>
          )}
        </DropdownMenu>
      }
    </Flex>
  )
}

export default FormSearchableSelect
